import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';
import { createProviderSettingsView } from './presentation/provider-settings.js';

let providerSettings = [];

const PROVIDER_SETTING_PREFIXES = ['llm.', 'image.', 'marketData.', 'linkfox.', 'aichat.', 'adOptimization.'];

const LABELS = {
  'llm.provider': '当前文本模型',
  'llm.minimax.agentModel': 'Agent 执行模型',
  'llm.minimax.generationModel': 'MiniMax 生成模型',
  'llm.minimax.reflectionModel': 'MiniMax 诊断模型',
  'llm.minimax.rpmLimit': 'MiniMax 每分钟请求参考值',
  'llm.minimax.tpmLimit': 'MiniMax 每分钟 Token 参考值',
  'aichat.model': '会话元数据模型（仅记录）',
  'marketData.provider': '市场数据源',
  'adOptimization.erpProvider': '广告优化 ERP 数据源',
  'adOptimization.operatingMode': '广告规则运行阶段',
  'adOptimization.shadowAutomationEnabled': '自动影子采样',
  'adOptimization.shadowDailySkuLimit': '每日新建 SKU 上限',
  'adOptimization.shadowMinimumSamples': '最低可判定样本数',
  'adOptimization.shadowMinimumSupportRate': '最低支持率',
  'adOptimization.shadowMinimumDistinctSkus': '最低独立 SKU 数',
  'adOptimization.shadowMinimumDistinctBaselineDates': '最低独立基线日期数',
  'linkfox.pollIntervalMs': 'LinkFox 查询间隔',
  'linkfox.pollTimeoutMs': 'LinkFox 最长等待',
  'linkfox.requestTimeoutMs': 'LinkFox 单次请求超时',
  'linkfox.suite.provider': '商品套图默认模型',
  'linkfox.suite.resolution': '商品套图默认分辨率',
  'linkfox.suite.extractPrePoint': '默认自动分析卖点',
  'linkfox.imageEditV2.provider': '尺寸图默认模型',
  'linkfox.imageEditV2.outputNum': '尺寸图默认张数',
  'linkfox.imageEditV2.resolution': '尺寸图默认分辨率',
  'linkfox.imageEditV2.supplyType': '尺寸图供应模式',
};

const HELP = {
  'llm.provider': 'Listing、翻译、诊断、Agent 和生图卖点提炼的新请求统一使用 MiniMax。',
  'llm.minimax.agentModel': 'AI 对话 Agent 实际执行使用的模型。需要支持 tool-calling 和多模态（图片附件），建议使用 MiniMax-M3。',
  'llm.maxConcurrency': '同时运行的 AI 文本任务数量。调高会更快，但也更容易触发服务商限流。',
  'aichat.model': '仅用于新建会话时写入元数据记录，不影响 Agent 实际执行模型（实际执行由上方 Agent 执行模型控制）。',
  'llm.minimax.rpmLimit': '只用于管理员观察和容量记录，不会主动拦截请求。',
  'llm.minimax.tpmLimit': '只用于管理员观察和容量记录，不会主动拦截请求。',
  'marketData.provider': '控制 ASIN、关键词等市场数据从哪个服务获取。',
  'adOptimization.erpProvider': '默认保持现有 SelectDB 链路。领星 Mock 仅用于开发验证；领星真实模式需要另行配置服务端凭证并完成字段映射，不会自动接管分析。',
  'adOptimization.operatingMode': '影子阶段自动收集和回看，不执行广告动作；正式阶段只读取已迁移的不可变规则快照，并停止新增影子样本。',
  'adOptimization.shadowAutomationEnabled': '每天 02:20 自动轮换选择近 30 天有真实广告数据的在售 SKU；同一 SKU 有 14 天冷却期。',
  'adOptimization.shadowDailySkuLimit': '自动任务每天最多新建多少个不同 SKU 的影子基线。',
  'adOptimization.shadowMinimumSamples': '仅计算已有明确支持或反向信号的建议；样本不足不能迁移。',
  'adOptimization.shadowMinimumSupportRate': '0.8 表示可判定样本中至少 80% 支持原规则判断。',
  'adOptimization.shadowMinimumDistinctSkus': '避免大量样本只来自少数 SKU，造成虚高准确率。',
  'adOptimization.shadowMinimumDistinctBaselineDates': '要求跨不同日期观察，降低单日异常对规则迁移的影响。',
  'linkfox.pollIntervalMs': '提交 LinkFox 任务后，每隔多久查询一次结果。',
  'linkfox.pollTimeoutMs': 'LinkFox 长时间无结果时，最多等待多久后判定超时。',
  'linkfox.requestTimeoutMs': '提交、查询、上传等单次 HTTP 请求的超时时间。',
  'linkfox.suite.provider': '控制 Step7 商品套图页打开时默认选中的 LinkFox providerName；用户仍可在页面内临时改。',
  'linkfox.suite.resolution': '控制 Step7 商品套图页打开时默认选中的分辨率。',
  'linkfox.suite.extractPrePoint': '打开后，新进入 Step7 时默认勾选“自动分析卖点”。',
  'linkfox.imageEditV2.provider': '控制尺寸图 / 智能修图 V2 默认提交的模型。',
  'linkfox.imageEditV2.outputNum': '控制尺寸图开启时默认生成几张，实际提交仍受页面开关控制。',
  'linkfox.imageEditV2.resolution': '控制尺寸图 / 智能修图 V2 默认分辨率。',
  'linkfox.imageEditV2.supplyType': 'eco 更省，stable 更稳；影响尺寸图 / 智能修图 V2。',
};

const VALUE_LABELS = {
  'llm.provider': { minimax: 'MiniMax 2.7 high-speed' },
  'llm.minimax.agentModel': { 'MiniMax-M3': 'MiniMax-M3（多模态+工具）', 'MiniMax-M2.7': 'MiniMax-M2.7', 'MiniMax-M2.7-highspeed': 'M2.7-highspeed（不支持图片）' },
  'llm.minimax.generationModel': { 'MiniMax-M2.7-highspeed': 'M2.7-highspeed', 'MiniMax-M2.7': 'MiniMax-M2.7', 'MiniMax-M3': 'MiniMax-M3' },
  'llm.minimax.reflectionModel': { 'MiniMax-M2.7-highspeed': 'M2.7-highspeed', 'MiniMax-M2.7': 'MiniMax-M2.7', 'MiniMax-M3': 'MiniMax-M3' },
  'marketData.provider': { sellersprite: '卖家精灵', sorftime: '数派', softime: '数派' },
  'adOptimization.erpProvider': {
    legacy_selectdb: '现有 SelectDB（默认）',
    lingxing_mock: '领星 Mock（仅开发）',
    lingxing: '领星（待授权）',
  },
  'adOptimization.operatingMode': {
    shadow: '影子采样（当前）',
    live: '正式规则（停止影子采样）',
  },
  'linkfox.suite.provider': {
    BANANA_PRO: 'BANANA_PRO（Pro版）',
    BANANA_2: 'BANANA_2（基础版2代）',
    WAN2_7: 'WAN2_7（万相2.7）',
    GPT_2_IMAGE: 'GPT_2_IMAGE（GPT图片2）',
    AIDRAW_EDIT: 'AIDRAW_EDIT（自研修图）',
  },
  'linkfox.suite.resolution': { '2K': '2K', '4K': '4K' },
  'linkfox.imageEditV2.provider': {
    BANANA_PRO: 'BANANA_PRO（Pro版）',
    BANANA_2: 'BANANA_2（基础版2代）',
    WAN2_7: 'WAN2_7（万相2.7）',
    GPT_2_IMAGE: 'GPT_2_IMAGE（GPT图片2）',
    AIDRAW_EDIT: 'AIDRAW_EDIT（自研修图）',
  },
  'linkfox.imageEditV2.resolution': { '1K': '1K', '2K': '2K', '4K': '4K' },
  'linkfox.imageEditV2.supplyType': { stable: 'stable（稳定）', eco: 'eco（经济）' },
};

const LINKFOX_PROVIDER_VALUES = ['BANANA_PRO', 'BANANA_2', 'WAN2_7', 'GPT_2_IMAGE', 'AIDRAW_EDIT'];
const CONTROL_ALLOWED_VALUES = {
  'llm.provider': ['minimax'],
  'llm.minimax.agentModel': ['MiniMax-M3', 'MiniMax-M2.7', 'MiniMax-M2.7-highspeed'],
  'llm.minimax.generationModel': ['MiniMax-M2.7-highspeed', 'MiniMax-M2.7', 'MiniMax-M3'],
  'llm.minimax.reflectionModel': ['MiniMax-M2.7-highspeed', 'MiniMax-M2.7', 'MiniMax-M3'],
  'marketData.provider': ['sellersprite', 'sorftime'],
  'adOptimization.erpProvider': ['legacy_selectdb', 'lingxing_mock', 'lingxing'],
  'adOptimization.operatingMode': ['shadow', 'live'],
  'linkfox.suite.provider': LINKFOX_PROVIDER_VALUES,
  'linkfox.suite.resolution': ['2K', '4K'],
  'linkfox.imageEditV2.provider': LINKFOX_PROVIDER_VALUES,
  'linkfox.imageEditV2.resolution': ['1K', '2K', '4K'],
  'linkfox.imageEditV2.supplyType': ['stable', 'eco'],
};

export function initProviderSettings() {
  registerViewLoader('admin-provider-settings', () => loadProviderSettings());
  document.getElementById('provider-settings-refresh-btn')?.addEventListener('click', () => loadProviderSettings());
  document.getElementById('provider-settings-save-btn')?.addEventListener('click', () => saveProviderSettings());
}

function isProviderSetting(item) {
  return PROVIDER_SETTING_PREFIXES.some((prefix) => String(item?.key || '').startsWith(prefix));
}

function settingMap() {
  return new Map(providerSettings.map((item) => [item.key, item]));
}

function getSetting(key) {
  return settingMap().get(key) || null;
}

function valueLabel(key, value) {
  return VALUE_LABELS[key]?.[String(value)] || String(value ?? '');
}

function normalizedControlValue(key, value) {
  if (key === 'marketData.provider' && String(value).toLowerCase() === 'softime') return 'sorftime';
  return value;
}

function allowedValuesForControl(key, item, options = {}) {
  const merged = [
    ...(Array.isArray(options.allowedValues) ? options.allowedValues : []),
    ...(Array.isArray(CONTROL_ALLOWED_VALUES[key]) ? CONTROL_ALLOWED_VALUES[key] : []),
    ...(Array.isArray(item?.allowedValues) ? item.allowedValues : []),
  ];
  if (!merged.length) return null;
  const values = [];
  for (const value of merged) {
    const normalized = normalizedControlValue(key, value);
    if (!values.some((itemValue) => String(itemValue) === String(normalized))) values.push(normalized);
  }
  const current = normalizedControlValue(key, item?.value);
  if (current != null && current !== '' && !values.some((value) => String(value) === String(current))) {
    values.push(current);
  }
  return values;
}

function renderProviderSettings() {
  const tbody = document.getElementById('provider-settings-tbody');
  if (!tbody) return;
  if (!providerSettings.length) {
    tbody.replaceChildren(createProviderSettingsStatusRow('暂无 Provider 配置'));
    return;
  }
  tbody.replaceChildren(createProviderSettingsView({
    getSetting,
    getLabel: (key) => LABELS[key] || key,
    getHelp: (key) => HELP[key] || '',
    getAllowedValues: allowedValuesForControl,
    normalizeControlValue: normalizedControlValue,
    valueLabel,
  }));
  bindProviderUi();
}

function createProviderSettingsStatusRow(message) {
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.colSpan = 5;
  const state = document.createElement('div');
  state.className = 'empty-state';
  const text = document.createElement('p');
  text.textContent = message;
  state.appendChild(text);
  cell.appendChild(state);
  row.appendChild(cell);
  return row;
}

function bindProviderUi() {
  document.querySelector('[data-provider-key="llm.provider"]')?.addEventListener('change', (ev) => {
    syncVisibleControlsIntoState();
    const item = getSetting('llm.provider');
    if (item) item.value = ev.target.value;
    renderProviderSettings();
  });
}

function syncVisibleControlsIntoState() {
  const byKey = settingMap();
  document.querySelectorAll('#provider-settings-tbody [data-provider-key]').forEach((el) => {
    const key = el.getAttribute('data-provider-key');
    const item = byKey.get(key);
    if (!item) return;
    const type = el.getAttribute('data-provider-type');
    item.value = type === 'boolean' ? el.checked : el.value;
  });
}

export async function loadProviderSettings() {
  const tbody = document.getElementById('provider-settings-tbody');
  if (tbody) tbody.replaceChildren(createProviderSettingsStatusRow('加载中…'));
  try {
    const res = await authFetch('/api/admin/runtime-settings');
    if (!res.ok) throw new Error('加载失败');
    const data = await res.json();
    providerSettings = (Array.isArray(data.settings) ? data.settings : []).filter(isProviderSetting);
    renderProviderSettings();
  } catch (err) {
    console.error(err);
    if (tbody) tbody.replaceChildren(createProviderSettingsStatusRow('加载失败'));
    showToast('加载 Provider 配置失败', 'error');
  }
}

async function saveProviderSettings() {
  syncVisibleControlsIntoState();
  const controls = Array.from(document.querySelectorAll('#provider-settings-tbody [data-provider-key]'));
  if (!controls.length) return;
  const settings = controls.map((el) => {
    const type = el.getAttribute('data-provider-type');
    return {
      key: el.getAttribute('data-provider-key'),
      value: type === 'boolean' ? el.checked : el.value,
    };
  });
  const btn = document.getElementById('provider-settings-save-btn');
  if (btn) btn.disabled = true;
  try {
    const res = await authFetch('/api/admin/runtime-settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || '保存失败');
    providerSettings = (Array.isArray(data.settings) ? data.settings : providerSettings).filter(isProviderSetting);
    renderProviderSettings();
    showToast('Provider 配置已保存', 'success');
  } catch (err) {
    console.error(err);
    showToast(err.message || '保存失败', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}
